import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOut } from '@fortawesome/pro-regular-svg-icons/faSignOut';

import useI18n from '@i18n';
import apiHooks, { useBalance } from '@apiHooks';
import { IGetUserProfileDataFormatted } from '@api/types';
import routes from '@config/routes.json';

import BaseLogo from '@base/BaseLogo';
import BaseContainer from '@base/BaseContainer';
import BaseSemanticLink from '@base/BaseSemanticLink';
import { rolesEnum } from '@HOCs/WithRole';
import { getUserRole } from '@utils/tokenManipulation';
import UserProfile from './UserProfile';
import BurgerMenu from './BurgerMenu';
import Navbar from './Navbar';

import { logout } from './utils';
import { authNavItems } from './Navbar/config';
import { currencies } from './congif';

export interface IDefaultHeaderProps {
    className?: string;
}

const { useUserProfile } = apiHooks;

const DefaultHeader: React.FC<IDefaultHeaderProps> = ({ className }) => {
    const { locale } = useI18n();

    const currentCurrency = currencies[1];
    const [userRole, setUserRole] = useState<rolesEnum | null>(null);

    const { data: userData } = useUserProfile<IGetUserProfileDataFormatted>();
    const { data: balanceData } = useBalance();
    const { name, profileImage } = userData || {};
    const { confirmed, hold } = useMemo(
        () =>
            balanceData?.balances.find(el => el.currency === currentCurrency.name) || {
                confirmed: 0,
                hold: 0,
            },
        [balanceData, currentCurrency],
    );

    useEffect(() => {
        setUserRole(getUserRole() as rolesEnum | null);
    }, []);

    const profile = {
        profileImage,
        currencyIcon: currentCurrency.icon,
        name,
        balance: confirmed,
        hold,
    };

    return (
        <header
            className={clsx('h-[60px] md:h-20 bg-white border-b border-blueGray-200 ', className)}
        >
            <BaseContainer>
                <div className="relative flex items-stretch justify-between h-full">
                    <div className="h-full md:hidden">
                        <BurgerMenu isAuth userRole={userRole}>
                            <BaseSemanticLink to={routes.profile}>
                                <UserProfile profile={profile} isBurger />
                            </BaseSemanticLink>
                        </BurgerMenu>
                    </div>
                    <div className="flex items-stretch h-full whitespace-nowrap">
                        <BaseLogo />
                        <div className="hidden h-full md:flex md:items-stretch">
                            <Navbar items={authNavItems[userRole]} />
                        </div>
                    </div>
                    <div className="flex items-stretch items-center h-full">
                        <div className="h-full md:flex md:items-stretch whitespace-nowrap">
                            <Link href={`/${locale}${routes.profile}`}>
                                <UserProfile profile={profile} />
                            </Link>
                        </div>
                        <button
                            onClick={logout}
                            type="button"
                            className="hidden md:block py-[28px] px-[18px] text-blueGray-300"
                        >
                            <FontAwesomeIcon icon={faSignOut} />
                        </button>
                    </div>
                </div>
            </BaseContainer>
        </header>
    );
};

export default DefaultHeader;
